/* eslint-disable max-lines-per-function */
import noop from 'lodash/noop';
import { useMemo } from 'react';

import { PrintTemplateSimple, PrintTemplateSimpleQuery } from '@dreryk/egabinet-document-template-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   JustifyContent,
   Padding,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';
import { Slot, TextPattern } from '@dreryk/watermelon';

import { consentTemplateActionTranslationKeys } from '../../../../../translations';

import { ConsentTemplateFillButton } from './cells/ConsentTemplateFillButton';
import { ConsentTemplateFinishButton } from './cells/ConsentTemplateFinishButton';
import { ConsentTemplateMarkAsFavoriteIcon } from './cells/ConsentTemplateMarkAsFavoriteIcon';
import { ConsentTemplateUnmarkAsFavoriteIcon } from './cells/ConsentTemplateUnmarkAsFavoriteIcon';

const PATIENT_CONSENT_CATEGORY = 'PATIENT_CONSENT';
const DEFAULT_ORDERINGS: Ordering<PrintTemplateSimple>[] = [
   {
      descending: true,
      property: 'favorite',
   },
   {
      descending: false,
      property: 'name',
   },
];

const t = consentTemplateActionTranslationKeys.modal.form.table.headers;

type UseConsentTemplateDataConfigurationOptions = {
   searchText: string | null;
   priorityTemplateIds: number[];
};

export const useConsentTemplateDataConfiguration = (
   options: UseConsentTemplateDataConfigurationOptions
): LazyLoadedDataConfigurationByCqrsQuery<PrintTemplateSimple> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PrintTemplateSimple>(() => {
      const query = new PrintTemplateSimpleQuery();
      query.categories = Slot.of([PATIENT_CONSENT_CATEGORY]);
      if (options.searchText) {
         query.name = TextPattern.anywhereIgnoreCase(options.searchText);
      }
      query.sortingPriorityTemplateIds = Slot.of(options.priorityTemplateIds);
      return query;
   }, DEFAULT_ORDERINGS);
};

type UseConsentTemplateTableColumnsOptions = {
   onApproveAndPrint: VoidFunction;
   onFavoriteChange: VoidFunction;
   patientId: number;
   visitId: number;
   priorityTemplateIds: number[];
};

// eslint-disable-next-line max-lines-per-function
export const useConsentTemplateTableColumns = (
   options: UseConsentTemplateTableColumnsOptions
): TableColumnConfiguration<PrintTemplateSimple>[] => {
   const prioritySet = useMemo(() => new Set<number>(options.priorityTemplateIds), [options.priorityTemplateIds]);

   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         favorite: '32px',
         name: '100%',
         options: '104px',
      });

      return [
         {
            name: 'favorite',
            accessor: noop,
            width: columnsWidth.favorite,
            sortingDisabled: true,
            padding: Padding.NO_RIGHT,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple>) => {
               if (bean.favorite) {
                  return (
                     <ConsentTemplateUnmarkAsFavoriteIcon
                        printTemplateId={bean.id}
                        onUnmarkedAsFavorite={options.onFavoriteChange}
                     />
                  );
               }

               return (
                  <ConsentTemplateMarkAsFavoriteIcon
                     printTemplateId={bean.id}
                     onMarkedAsFavorite={options.onFavoriteChange}
                  />
               );
            },
         },
         {
            name: 'name',
            label: t.name,
            width: columnsWidth.name,
            accessor: noop,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple>) => (
               <Text testId={'name'} maxLines={3}>
                  {bean.name}
               </Text>
            ),
         },
         {
            width: columnsWidth.options,
            justifyContent: JustifyContent.START,
            accessor: noop,
            name: 'options',
            sortingDisabled: true,

            renderer: ({ bean }: ValueRendererProps<PrintTemplateSimple, void>) => {
               if (prioritySet.has(bean.id)) {
                  return (
                     <ConsentTemplateFinishButton
                        onDocumentTemplateOpen={options.onApproveAndPrint}
                        printTemplateName={bean.name}
                        printTemplateId={bean.id}
                        visitId={options.visitId}
                        testId={`finish-${bean.id}-${bean.name}`}
                     />
                  );
               }

               return (
                  <ConsentTemplateFillButton
                     onDocumentTemplateOpen={options.onApproveAndPrint}
                     printTemplateName={bean.name}
                     printTemplateId={bean.id}
                     visitId={options.visitId}
                     testId={`${bean.id}-${bean.name}`}
                  />
               );
            },
         },
      ];
   }, [options.onFavoriteChange, options.onApproveAndPrint, options.visitId, prioritySet]);
};
